import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Navigate,
  useParams,
  useSearchParams,
  Link as RouterLink,
} from 'react-router-dom'

import {
  CancelOutlined,
  CheckCircleOutlined,
  AccountBalanceOutlined,
  Description,
  FileDownloadOutlined,
  ContentCopy,
  Done,
  PriorityHigh,
} from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  AlertTitle,
  Box,
  Container,
  Link,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  Grid,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material'

import L from '~/components/Helpers/Lang'
import { routeNames } from '~/constants/uiRouteNames'
import { PURCHASE_INTENTS, ACCOUNT } from '~/constants'

import useClipboard from '~/hooks/useClipboard'
import { getInvoiceDownloadToken } from '~/actions/accountActions'
import { getOpenPayments } from '~/actions/intentActions'
import { priceFormat } from '~/utils/numberFormat'
import { useDownloadToken } from '~/hooks/useTokenDownload'

const PaymentStatus = {
  SUCCESS: 'success',
  CANCELED: 'canceled',
  BANK_TRANSFER: 'bank-transfer',
}

const colorForPaymentStatus = {
  [PaymentStatus.SUCCESS]: 'success',
  [PaymentStatus.CANCELED]: 'error',
  [PaymentStatus.BANK_TRANSFER]: 'info',
}

const iconForPaymentStatus = {
  [PaymentStatus.SUCCESS]: CheckCircleOutlined,
  [PaymentStatus.CANCELED]: CancelOutlined,
  [PaymentStatus.BANK_TRANSFER]: AccountBalanceOutlined,
}

const labelForPaymentStatus = {
  [PaymentStatus.SUCCESS]: 'successful',
  [PaymentStatus.CANCELED]: 'canceled',
  [PaymentStatus.BANK_TRANSFER]: 'bank-transfer',
}

const linkUrlForPaymentStatus = {
  [PaymentStatus.SUCCESS]: `/${routeNames.account}/${routeNames.purchases}`,
  [PaymentStatus.CANCELED]: `/${routeNames.purchase}`,
  [PaymentStatus.BANK_TRANSFER]: `/${routeNames.account}/${routeNames.purchases}`, // Or back to dashboard
}

const PaymentResult = () => {
  const { status } = useParams()
  const [searchParams] = useSearchParams()
  const paymentId = searchParams.get('payment_id')

  const dispatch = useDispatch()
  const openPayments = useSelector((state) => state.intents.openPayments)

  // Local state to track if we're loading bank details
  const [loading, setLoading] = useState(false)
  const downloadingToken = useSelector((state) => state.account.downloadingToken)
  const { getDownloadToken, DownloadComponent } = useDownloadToken()

  // Fetch open payments if we are on bank transfer page to get details
  useEffect(() => {
    if (status === PaymentStatus.BANK_TRANSFER) {
      // Clear the cart on the frontend since the payment is now initiated
      dispatch({ type: PURCHASE_INTENTS.CLEAR_INTENTS })
      dispatch({ type: ACCOUNT.SET_INTENT_COUNT, count: 0 })

      setLoading(true)
      dispatch(getOpenPayments()).finally(() => setLoading(false))
    }
  }, [status, dispatch])

  const [handleCopy, lastCopied] = useClipboard()
  const CopyButton = ({ text, ...rest }) => (
    <Tooltip placement="top" title={<L term="button.copy-to-clipboard" />}>
      <IconButton onClick={() => handleCopy(text)} {...rest}>
        {lastCopied === text ? <Done color="success" /> : <ContentCopy />}
      </IconButton>
    </Tooltip>
  )

  if (!Object.values(PaymentStatus).includes(status)) {
    return <Navigate to="/" />
  }

  const Icon = iconForPaymentStatus[status]

  // Find the specific payment for bank transfer details
  const currentPayment =
    status === PaymentStatus.BANK_TRANSFER
      ? openPayments?.find((p) => p.payment_id === parseInt(paymentId))
      : null

  const renderBankDetails = () => {
    if (loading) {
      return <CircularProgress />
    }

    if (!currentPayment) {
      return (
        <Typography>
          <L term="label.no-payment-details" />
        </Typography>
      )
    }

    const {
      bank_transfer_details: bank,
      payment_reference,
      total,
      currency,
    } = currentPayment

    if (!bank) {
      return (
        <Typography>
          <L term="label.bank-transfer-missing-details" />
        </Typography>
      )
    }

    const handleDownloadProforma = () => {
      getDownloadToken(getInvoiceDownloadToken, {
        id: paymentId,
        type: 'proforma',
      })
    }

    return (
      <Paper elevation={3} sx={{ p: 3, mt: 3, width: '100%' }}>
        <Typography variant="h6" gutterBottom>
          <L term="label.bank-transfer-amount" />:{' '}
          <strong>{priceFormat(total, currency)}</strong>
        </Typography>

        {bank.proforma_number && (
          <Box mb={2}>
            <LoadingButton
              variant="outlined"
              startIcon={<Description />}
              onClick={handleDownloadProforma}
              loading={downloadingToken}
              loadingPosition="start"
              fullWidth
            >
              <L term="button.proforma" />
            </LoadingButton>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography color="textSecondary">
              <L term="label.bank-transfer-beneficiary" />:
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Typography>
              <span>{bank.account_holder}</span>
              <CopyButton
                size="small"
                sx={{ pl: 1 }}
                text={bank.account_holder}
              />
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography color="textSecondary">
              <L term="label.bank-transfer-bank-name" />:
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Typography>{bank.bank_name}</Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography color="textSecondary">
              <L term="label.bank-transfer-iban" />:
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Typography sx={{ fontFamily: 'monospace' }}>
              <span>{bank.iban}</span>
              <CopyButton size="small" sx={{ pl: 1 }} text={bank.iban} />
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography color="textSecondary">
              <L term="label.bank-transfer-bic-swift" />:
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Typography sx={{ fontFamily: 'monospace' }}>{bank.bic}</Typography>
          </Grid>

          <Grid item xs={12} sm={12} sx={{ mt: 1 }}>
            <Alert icon={<PriorityHigh />} severity="info">
              <AlertTitle>Important</AlertTitle>
              <Typography>
                <L term="label.bank-transfer-reference" />:
              </Typography>
            </Alert>
            <OutlinedInput
              fullWidth
              endAdornment={
                <InputAdornment position="end">
                  <CopyButton edge="end" text={payment_reference} />
                </InputAdornment>
              }
              inputProps={{
                sx: (theme) => ({
                  color: theme.palette.primary.main,
                  fontWeight: 'bold',
                  textAlign: 'center',
                }),
              }}
              sx={(theme) => ({ background: theme.palette.background.paper })}
              value={payment_reference}
            />
          </Grid>
        </Grid>
      </Paper>
    )
  }

  return (
    <Container
      maxWidth="sm"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        py: 4,
      }}
    >
      <Icon
        color={colorForPaymentStatus[status]}
        sx={{ width: 128, height: 128 }}
      />
      <Typography variant="h5" textAlign="center" gutterBottom>
        <L term={`label.${labelForPaymentStatus[status]}-purchase`} />
      </Typography>

      {status === PaymentStatus.BANK_TRANSFER ? (
        <>
          {renderBankDetails()}

          <Box mt={3}>
            <Link component={RouterLink} to="/" variant="button">
              <L term="button.back-to-products" />
            </Link>
          </Box>
        </>
      ) : (
        <Link
          component={RouterLink}
          variant="subtitle1"
          to={linkUrlForPaymentStatus[status]}
        >
          <L term={`text.payment-${status}`} />
        </Link>
      )}
      {DownloadComponent}
    </Container>
  )
}

export default PaymentResult
